// src/components/YahooReauthBanner.tsx
//
// Yahoo!ショッピングのAPI連携（YConnect OAuth）は再認可期限（保存から約4週間）があり、
// 期限が切れると注文データの取得が黙って止まる。設定画面を開かなくても気付けるよう、
// Yahoo表示中の全ページ上部に期限警告バナーを出す。
// 期限7日前から黄色の注意、期限切れは赤の警告（role="alert"）。

import React, { useEffect, useState } from "react";
import { useMallContext } from "../contexts/MallContext";
import { apiGetJson } from "../utils/apiClient";

const WARN_DAYS = 7;

type YahooOauthStatus = {
    ok: boolean;
    connected: boolean;
    reauthorizeBy: null | string;
};

export default function YahooReauthBanner({ onNavigateToSettings }: { onNavigateToSettings: () => void }) {
    const { mall, hasYahoo } = useMallContext();
    const [reauthorizeBy, setReauthorizeBy] = useState<null | string>(null);
    const [dismissed, setDismissed] = useState(false);

    const isYahoo = mall === "yahoo";

    useEffect(() => {
        if (!isYahoo || !hasYahoo) {
            setReauthorizeBy(null);
            return;
        }
        let cancelled = false;
        apiGetJson<YahooOauthStatus>("/api/auth/yahoo/status")
            .then((s) => {
                if (cancelled) return;
                setReauthorizeBy(s?.connected ? (s.reauthorizeBy ?? null) : null);
            })
            .catch(() => {
                // 取得失敗時はバナーを出さない（設定画面側で確認できる）
                if (!cancelled) setReauthorizeBy(null);
            });
        return () => {
            cancelled = true;
        };
    }, [isYahoo, hasYahoo]);

    if (!isYahoo || !reauthorizeBy || dismissed) return null;

    const deadline = new Date(reauthorizeBy);
    if (Number.isNaN(deadline.getTime())) return null;
    const daysLeft = Math.floor((deadline.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    if (daysLeft > WARN_DAYS) return null;

    const expired = daysLeft < 0;
    const deadlineLabel = `${deadline.getFullYear()}/${deadline.getMonth() + 1}/${deadline.getDate()}`;

    return (
        <div
            role={expired ? "alert" : "status"}
            className={`mb-4 rounded-lg border px-4 py-3 flex items-start justify-between gap-3 ${
                expired ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"
            }`}
        >
            <div className={`text-sm ${expired ? "text-red-800" : "text-amber-900"}`}>
                {expired ? (
                    <>
                        <span className="font-bold">Yahoo!ショッピングのAPI連携の有効期限が切れています。</span>
                        注文データの取得が止まっています。「Yahoo連携設定」から再認可（再度「許可」を押す操作）を行ってください。
                    </>
                ) : (
                    <>
                        <span className="font-bold">
                            Yahoo!ショッピングのAPI連携の有効期限が近づいています（{deadlineLabel} まで・あと{daysLeft}日）。
                        </span>
                        期限までに「Yahoo連携設定」から再認可を行うと、データ取得が途切れません。
                    </>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    onClick={onNavigateToSettings}
                    className={`rounded px-3 py-1 text-xs font-semibold text-white ${
                        expired ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"
                    }`}
                >
                    Yahoo連携設定へ
                </button>
                {!expired && (
                    <button
                        type="button"
                        onClick={() => setDismissed(true)}
                        className="rounded border bg-white px-2 py-1 text-xs text-slate-600 hover:bg-gray-50"
                        aria-label="この警告を閉じる"
                    >
                        閉じる
                    </button>
                )}
            </div>
        </div>
    );
}
